import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Plus, Search, MoreHorizontal, Edit, Trash2, CheckCircle,
  UserPlus, Sparkles, Phone, Mail as MailIcon, MapPin
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const CATEGORIES = [
  'All', 'Owner', 'Chef', 'GM', 'FOH', 'BOH',
  'Commercial Landlord', 'Residential Landlord', 'Staff Housing Landlord',
  'RE Broker', 'Hotel', 'Investor', 'Supplier', 'Event Planner', 'Press'
];

const VILLAGES = [
  'Southampton', 'East Hampton', 'Bridgehampton', 'Montauk',
  'Sag Harbor', 'Amagansett', 'Wainscott', 'Water Mill',
  'Quogue', 'Hampton Bays', 'Shelter Island', 'Other'
];

const STATUS_STYLES = {
  overdue: 'badge-overdue',
  due_today: 'badge-due-today',
  on_track: 'badge-on-track',
  completed: 'badge-on-track',
  stalled: 'badge-overdue',
};

const fmtDate = (d) => { try { return d ? format(parseISO(d), 'MMM d, yyyy') : '-'; } catch { return '-'; } };

const EMPTY_FORM = { name: '', category: '', restaurant_name: '', phone: '', email: '', village: '', notes: '', next_followup_date: '' };

export default function Contacts() {
  const [searchParams] = useSearchParams();
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState(searchParams.get('category') || 'All');
  const [showModal, setShowModal] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [loadingAi, setLoadingAi] = useState(false);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (category !== 'All') params.category = category;
      if (search) params.search = search;
      const res = await api.get('/api/contacts', { params });
      setContacts(res.data.contacts || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load contacts'); }
    finally { setLoading(false); }
  }, [category, search]);

  useEffect(() => {
    const timer = setTimeout(loadContacts, 300);
    return () => clearTimeout(timer);
  }, [loadContacts]);

  const openAdd = () => { setEditContact(null); setForm(EMPTY_FORM); setAiSuggestion(null); setShowModal(true); };
  const openEdit = (c) => {
    setEditContact(c);
    setForm({
      name: c.name || '', category: c.category || '', restaurant_name: c.restaurant_name || '',
      phone: c.phone || '', email: c.email || '', village: c.village || '',
      notes: c.notes || '', next_followup_date: c.next_followup_date ? c.next_followup_date.slice(0, 10) : ''
    });
    setAiSuggestion(null);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.category) { toast.error('Name and category are required'); return; }
    setSaving(true);
    try {
      if (editContact) {
        await api.put(`/api/contacts/${editContact.id}`, form);
        toast.success('Contact updated');
      } else {
        await api.post('/api/contacts', form);
        toast.success('Contact added & outreach sequence created');
      }
      setShowModal(false);
      loadContacts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save contact');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contact and their outreach sequence?')) return;
    try {
      await api.delete(`/api/contacts/${id}`);
      toast.success('Contact deleted');
      loadContacts();
    } catch { toast.error('Failed to delete'); }
  };

  const markContacted = async (c) => {
    try {
      const nextDate = new Date();
      nextDate.setDate(nextDate.getDate() + 7);
      await api.patch(`/api/contacts/${c.id}/followup`, {
        next_followup_date: nextDate.toISOString()
      });
      toast.success('Marked as contacted — next follow-up in 7 days');
      loadContacts();
    } catch { toast.error('Failed to update'); }
  };

  const getAiSuggestion = async (contactId) => {
    setLoadingAi(true);
    try {
      const res = await api.post(`/api/ai/outreach-suggestion/${contactId}`);
      setAiSuggestion(res.data.suggestion);
    } catch { toast.error('Failed to get AI suggestion'); }
    finally { setLoadingAi(false); }
  };

  return (
    <div className="space-y-5">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className="page-title">Contacts</h1>
          <p className="page-subtitle">{total} contacts across 14 categories</p>
        </div>
        <Button onClick={openAdd} data-testid="contacts-add-button" className="bg-primary">
          <Plus size={16} className="mr-1.5" /> Add Contact
        </Button>
      </div>

      {/* Category tabs */}
      <div className="overflow-x-auto">
        <div className="flex gap-1.5 min-w-max pb-1">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                category === cat
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-secondary'
              }`}
              data-testid={`category-tab-${cat.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search contacts..."
          className="pl-9"
          data-testid="contacts-search"
        />
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : contacts.length === 0 ? (
            <div className="text-center py-12">
              <UserPlus size={40} className="mx-auto mb-3 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">No contacts found.</p>
              <Button onClick={openAdd} variant="outline" size="sm" className="mt-3">Add first contact</Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Restaurant / Affiliation</TableHead>
                    <TableHead>Village</TableHead>
                    <TableHead>Follow-up Status</TableHead>
                    <TableHead>Last Contact</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contacts.map(c => (
                    <TableRow key={c.id}>
                      <TableCell className="font-medium">
                        <div>{c.name}</div>
                        <div className="flex items-center gap-2 mt-0.5">
                          {c.phone && <span className="text-xs text-muted-foreground flex items-center gap-1"><Phone size={10} />{c.phone}</span>}
                          {c.email && <span className="text-xs text-muted-foreground flex items-center gap-1"><MailIcon size={10} />{c.email}</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="text-xs">{c.category}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{c.restaurant_name || '-'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {c.village ? <span className="flex items-center gap-1"><MapPin size={10} />{c.village}</span> : '-'}
                      </TableCell>
                      <TableCell>
                        {c.followup_status && (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[c.followup_status] || 'badge-on-track'}`}>
                            {c.followup_status?.replace('_', ' ')}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{fmtDate(c.last_contact_date)}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="w-8 h-8">
                              <MoreHorizontal size={16} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEdit(c)}>
                              <Edit size={14} className="mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => markContacted(c)}>
                              <CheckCircle size={14} className="mr-2" /> Mark Contacted
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { openEdit(c); setTimeout(() => getAiSuggestion(c.id), 100); }}>
                              <Sparkles size={14} className="mr-2" /> AI Outreach Tip
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(c.id)} className="text-destructive">
                              <Trash2 size={14} className="mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {editContact ? 'Edit Contact' : 'Add New Contact'}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Full Name *</Label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="John Smith" data-testid="contact-form-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Category *</Label>
              <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                <SelectTrigger data-testid="contact-form-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.slice(1).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Restaurant / Affiliation</Label>
              <Input value={form.restaurant_name} onChange={e => setForm(p => ({ ...p, restaurant_name: e.target.value }))} placeholder="The Barn at Mecox" />
            </div>
            <div className="space-y-1.5">
              <Label>Village</Label>
              <Select value={form.village} onValueChange={v => setForm(p => ({ ...p, village: v }))}>
                <SelectTrigger><SelectValue placeholder="Select village" /></SelectTrigger>
                <SelectContent>
                  {VILLAGES.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="+1 631 555 0100" />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="john@restaurant.com" />
            </div>
            <div className="space-y-1.5">
              <Label>Next Follow-up Date</Label>
              <Input type="date" value={form.next_followup_date} onChange={e => setForm(p => ({ ...p, next_followup_date: e.target.value }))} />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Met at Sunset Beach, interested in summer pop-up concept..." rows={3} />
            </div>
          </div>

          {/* AI Suggestion */}
          {editContact && (
            <div className="mt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => getAiSuggestion(editContact.id)}
                disabled={loadingAi}
                className="w-full"
                data-testid="contact-ai-outreach-button"
              >
                <Sparkles size={14} className="mr-1.5" />
                {loadingAi ? 'Generating...' : 'Get AI Outreach Suggestion'}
              </Button>
              {aiSuggestion && (
                <div className="mt-2 p-3 rounded-lg bg-accent/30 border border-accent text-sm text-foreground">
                  {aiSuggestion}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-primary" data-testid="contact-edit-save-button">
              {saving ? 'Saving...' : editContact ? 'Save Changes' : 'Add Contact'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
